import {
  SPAWN_WEIGHTS,
  GAME_WIDTH,
  GAME_HEIGHT,
  SPAWN_CLEARANCE_MIN,
  SPAWN_CLEARANCE_RETRY,
  ENTITY_RADIUS,
  ENTITY_CAP,
  DIFFICULTY,
  POWER_UP,
} from '../config/gameConfig.js';
export const EntityKind = {
  COLLECTIBLE: 'collectible',
  WBC: 'wbc',
  ANTIBODY: 'antibody',
  PLATELET: 'platelet',
  POWER_SHIELD: 'powerShield',
  POWER_SPEED: 'powerSpeed',
  POWER_INVIS: 'powerInvis',
  POWER_SLOW: 'powerSlow',
};

/**
 * Weighted spawn selection + clearance checks.
 */
export class Spawner {
  /** @param {import('./EntityPools.js').EntityPools} pools */
  constructor(pools) {
    this.pools = pools;
    this.accumulator = 0;
    this.lastLoggedDifficulty = 0;
    /** @type {{ attempted: number; spawned: number }} */
    this.stats = { attempted: 0, spawned: 0 };
  }

  pickKind(difficulty, elapsedMinutes) {
    const w = { ...SPAWN_WEIGHTS };
    const bias = 1 + elapsedMinutes * DIFFICULTY.weightBiasPerMinute * difficulty * 0.25;
    w.wbc *= bias * 1.15;
    w.antibody *= bias * 1.05;
    w.platelet *= 1 + elapsedMinutes * 0.04;
    w.collectible /= 1 + elapsedMinutes * 0.03;

    let total = 0;
    for (const k of Object.keys(w)) total += w[k];

    let r = Math.random() * total;
    for (const [key, wt] of Object.entries(w)) {
      r -= wt;
      if (r <= 0) return key;
    }
    return EntityKind.PLATELET;
  }

  /** @param {import('../entities/Player.js').Player} player */
  clearanceOk(px, py, pr, sx, sy, sr) {
    const min = SPAWN_CLEARANCE_MIN;
    const dx = sx - px;
    const dy = sy - py;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist - (sr + pr) < min) return false;

    for (const pool of this.pools.gamePools) {
      for (const img of pool.iterateActive()) {
        const r = img.poolData.radius ?? ENTITY_RADIUS.collectible;
        const ex = img.x - sx;
        const ey = img.y - sy;
        const d = Math.sqrt(ex * ex + ey * ey);
        if (d < sr + r + 14) return false;
      }
    }
    return true;
  }

  trySpawn(kind, player, difficulty) {
    const pools = this.pools;
    if (pools.totalGameplayActive() >= ENTITY_CAP) return false;

    let pool = null;
    let radius = ENTITY_RADIUS.collectible;
    switch (kind) {
      case EntityKind.COLLECTIBLE:
        pool = pools.collectibles;
        radius = ENTITY_RADIUS.collectible;
        break;
      case EntityKind.WBC:
        pool = pools.wbc;
        radius = ENTITY_RADIUS.wbc;
        break;
      case EntityKind.ANTIBODY:
        pool = pools.antibodies;
        radius = ENTITY_RADIUS.antibody;
        break;
      case EntityKind.PLATELET:
        pool = pools.platelets;
        radius = ENTITY_RADIUS.platelet;
        break;
      case EntityKind.POWER_SHIELD:
        pool = pools.powerShield;
        radius = ENTITY_RADIUS.powerUp;
        break;
      case EntityKind.POWER_SPEED:
        pool = pools.powerSpeed;
        radius = ENTITY_RADIUS.powerUp;
        break;
      case EntityKind.POWER_INVIS:
        pool = pools.powerInvis;
        radius = ENTITY_RADIUS.powerUp;
        break;
      case EntityKind.POWER_SLOW:
        pool = pools.powerSlow;
        radius = ENTITY_RADIUS.powerUp;
        break;
      default:
        return false;
    }

    const margin = 36 + Math.random() * 70;
    const sx = GAME_WIDTH + margin;
    const sy = radius + 24 + Math.random() * (GAME_HEIGHT - (radius + 48));

    let placed = false;
    for (let i = 0; i < SPAWN_CLEARANCE_RETRY; i++) {
      const jitterY = (Math.random() - 0.5) * 56;
      const tryY = Math.max(
        radius + 16,
        Math.min(GAME_HEIGHT - radius - 16, sy + jitterY)
      );
      const tryX = sx + i * 18;
      if (this.clearanceOk(player.x, player.y, player.radius, tryX, tryY, radius)) {
        const img = pool.acquire(tryX, tryY, 8);
        img.poolData.kind = kind;
        img.poolData.radius = radius;
        img.poolData.wobblePhase = Math.random() * Math.PI * 2;
        img.poolData.seekMul = 0.92 + Math.random() * 0.14;
        if (kind === EntityKind.POWER_SHIELD) img.poolData.powerDuration = POWER_UP.shield;
        if (kind === EntityKind.POWER_SPEED) img.poolData.powerDuration = POWER_UP.speedBoost;
        if (kind === EntityKind.POWER_INVIS) img.poolData.powerDuration = POWER_UP.invisibility;
        if (kind === EntityKind.POWER_SLOW) img.poolData.powerDuration = POWER_UP.slowMotion;
        placed = true;
        break;
      }
    }
    return placed;
  }

  resetStats() {
    this.stats = { attempted: 0, spawned: 0 };
    this.accumulator = 0;
  }
}
